import * as React from 'react';
import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { Context } from '@grandvisionhq/carts-v2';
import { toast } from '@grandvisionhq/common';
import { Hooks } from '@grandvisionhq/payments';
import { useIntl } from '@grandvisionhq/state';
import { convertCentsToDecimalString } from '@grandvisionhq/utils';
import { useCheckoutSettings } from '../../../../../context/CheckoutSettings/context.js';
import '@swc/helpers';
import { CheckoutErrorsMapping } from '../../../../../mappers/errors-mapping.js';
import { mapCartExpressPaymentMethods, mapCartRegularPaymentMethods } from '../mappers/apple-pay-payment-methods.js';
import { useMapShippingContact } from '../utility-hooks/use-map-shipping-contact.js';
import { useRequiredContactFields } from '../utility-hooks/use-required-contact-fields.js';

var ShippingContactEditingMode;
(function(ShippingContactEditingMode) {
    ShippingContactEditingMode["STORE_PICKUP"] = 'storePickup';
    ShippingContactEditingMode["DEFAULT"] = 'enabled';
})(ShippingContactEditingMode || (ShippingContactEditingMode = {}));
const useCreatePaymentRequest = (isExpressPayment = true)=>{
    var ref5, ref1;
    const { country , currency  } = useIntl();
    const [getCartExpressPaymentMethods, paymentMethodsResult] = Hooks.useGetCartExpressPaymentMethods();
    const [getPaymentMethods, queryResult] = Hooks.useGetCartPaymentMethods();
    const { cart  } = Context.useCart();
    const requiredContactFields = useRequiredContactFields();
    const { shippingContact , shippingContactEditingMode , shippingType  } = useMapShippingContact(cart);
    const { payments  } = useCheckoutSettings();
    const [error, setError] = React.useState(undefined);
    var ref2;
    const expressPaymentMethods = React.useMemo(()=>{
        return (ref2 = paymentMethodsResult === null || paymentMethodsResult === void 0 ? void 0 : paymentMethodsResult.data) !== null && ref2 !== void 0 ? ref2 : [];
    }, [
        paymentMethodsResult === null || paymentMethodsResult === void 0 ? void 0 : paymentMethodsResult.data
    ]);
    var _data;
    const regularPaymentMethods = React.useMemo(()=>(_data = queryResult.data) !== null && _data !== void 0 ? _data : []
    , [
        queryResult.data
    ]);
    const handlePaymentMethodsBrandSettings = React.useCallback((cartId)=>{
        if (cartId) {
            isExpressPayment ? getCartExpressPaymentMethods(cartId) : getPaymentMethods(cartId);
        }
    }, [
        getCartExpressPaymentMethods,
        getPaymentMethods,
        isExpressPayment
    ]);
    React.useEffect(()=>{
        if (cart === null || cart === void 0 ? void 0 : cart.id) {
            handlePaymentMethodsBrandSettings(cart.id);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        cart === null || cart === void 0 ? void 0 : cart.id
    ]);
    const createPaymentRequest = React.useCallback(()=>{
        var ref, ref3, ref4;
        if (!(cart === null || cart === void 0 ? void 0 : cart.id)) {
            console.error('No cart available.');
            setError(CheckoutErrorsMapping.CartIdNotFound);
            toast.failed(CheckoutErrorsMapping.CartIdNotFound);
            return;
        }
        if (!(paymentMethodsResult === null || paymentMethodsResult === void 0 ? void 0 : paymentMethodsResult.data)) {
            console.error('No payment methods available.');
            setError(CheckoutErrorsMapping.PaymentMethodNotFound);
            toast.failed(CheckoutErrorsMapping.PaymentMethodNotFound);
            return;
        }
        if (!(payments === null || payments === void 0 ? void 0 : (ref = payments.applePay) === null || ref === void 0 ? void 0 : ref.merchantName)) {
            console.error('No merchant name available.');
        }
        const totalPrice = cart === null || cart === void 0 ? void 0 : (ref3 = cart.prices) === null || ref3 === void 0 ? void 0 : ref3.totalPrice;
        const total = {
            label: (payments === null || payments === void 0 ? void 0 : (ref4 = payments.applePay) === null || ref4 === void 0 ? void 0 : ref4.merchantName) || 'GrandVision N.V.',
            amount: convertCentsToDecimalString(totalPrice),
            type: 'final'
        };
        const merchantCapabilities = new Array('supports3DS');
        const paymentRequest = {
            requiredShippingContactFields: requiredContactFields,
            requiredBillingContactFields: requiredContactFields,
            countryCode: country,
            currencyCode: currency,
            merchantCapabilities,
            total,
            supportedNetworks: isExpressPayment ? mapCartExpressPaymentMethods(expressPaymentMethods) : mapCartRegularPaymentMethods(regularPaymentMethods),
            shippingContact,
            shippingType,
            shippingContactEditingMode
        };
        // remove all undefined properties and return
        return pickBy(paymentRequest, identity);
    }, [
        cart === null || cart === void 0 ? void 0 : cart.id,
        cart === null || cart === void 0 ? void 0 : (ref5 = cart.prices) === null || ref5 === void 0 ? void 0 : ref5.totalPrice,
        paymentMethodsResult === null || paymentMethodsResult === void 0 ? void 0 : paymentMethodsResult.data,
        payments === null || payments === void 0 ? void 0 : (ref1 = payments.applePay) === null || ref1 === void 0 ? void 0 : ref1.merchantName,
        requiredContactFields,
        country,
        currency,
        isExpressPayment,
        expressPaymentMethods,
        regularPaymentMethods,
        shippingContact,
        shippingType,
        shippingContactEditingMode, 
    ]);
    return [
        createPaymentRequest,
        {
            error,
            loading: paymentMethodsResult.loading || false
        }, 
    ];
};

export { useCreatePaymentRequest };
//# sourceMappingURL=use-create-payment-request.js.map
